import { PdfElement, PdfViewerProps, PdfViewerState, UsePdfViewerState } from 'common/pdf-viewer/pdf-viewer.types';
import { createContext, Dispatch, FC, useContext, useReducer, ReactChild } from 'react';
import { useUpdateEffect } from 'react-use';

const initialState: PdfViewerState = { elements: [], isSideBarVisible: true };
type Action =
  | { payload?: PdfElement; type: 'SELECT_ELEMENT_ACTION' }
  | { payload: PdfElement[]; type: 'SET_ELEMENTS_ACTION' }
  | { type: 'TOGGLE_SIDEBAR_VISIBLE_ACTION' };

// eslint-disable-next-line @typescript-eslint/default-param-last
function reducer(state: PdfViewerState = initialState, action: Action): PdfViewerState {
  switch (action.type) {
    case 'SET_ELEMENTS_ACTION':
      return { ...state, elements: action.payload };
    case 'SELECT_ELEMENT_ACTION':
      return state?.elementSelected?.url !== action?.payload?.url
        ? { ...state, elementSelected: action.payload }
        : state;
    case 'TOGGLE_SIDEBAR_VISIBLE_ACTION':
      return { ...state, isSideBarVisible: !state.isSideBarVisible };
    default:
      return state;
  }
}

const PdfViewerStateContext = createContext<PdfViewerState>(initialState);
const PdfViewerDispatchContext = createContext<Dispatch<Action> | undefined>(undefined);

export const PdfViewerProvider: FC<PdfViewerProps> = props => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    elementSelected: props?.elements?.[0],
    elements: props?.elements || [],
  });

  useUpdateEffect(() => {
    dispatch({ payload: props?.elements || [], type: 'SET_ELEMENTS_ACTION' });
    dispatch({ payload: props?.elements?.[0], type: 'SELECT_ELEMENT_ACTION' });
  }, [props?.elements]);

  return (
    <PdfViewerStateContext.Provider value={state}>
      <PdfViewerDispatchContext.Provider value={dispatch}>{props.children}</PdfViewerDispatchContext.Provider>
    </PdfViewerStateContext.Provider>
  );
};

export const PdfViewerConsumer: FC<{ children: (state: PdfViewerState) => ReactChild }> = ({ children }) => (
  <PdfViewerStateContext.Consumer>{value => children(value)}</PdfViewerStateContext.Consumer>
);

export function usePdfViewerState(): UsePdfViewerState {
  const context = useContext(PdfViewerStateContext);
  const dispatch = usePdfViewerDispatch();

  if (context === undefined) {
    throw new Error(`usePdfViewerState must be used inside a PdfViewerProvider`);
  }

  return { ...context, toggleShowSidebar: () => dispatch({ type: 'TOGGLE_SIDEBAR_VISIBLE_ACTION' }) };
}

export function usePdfViewerDispatch(): Dispatch<Action> {
  const context = useContext(PdfViewerDispatchContext);

  if (context === undefined) {
    throw new Error(`usePdfViewerDispatch must be used inside a PdfViewerProvider`);
  }

  return context;
}
